<script setup>
import { computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();

const state = reactive({
  channelName: '',
  appId: '',
  token: '',
  accountApiKey: '',
  serverError: '',
});

const rules = {
  channelName: { required },
  appId: { required },
  token: { required },
  accountApiKey: { required },
};

const v$ = useVuelidate(rules, state);

const uiFlags = computed(() => store.getters['inboxes/getUIFlags']);

const resetErrors = () => {
  state.serverError = '';
};

const createChannel = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    state.serverError = '';
    const iungoChannel = await store.dispatch('inboxes/createChannel', {
      name: state.channelName?.trim(),
      channel: {
        type: 'iungo',
        app_id: state.appId,
        token: state.token,
        account_api_key: state.accountApiKey,
        api_key: state.accountApiKey,
      },
    });

    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: iungoChannel.id,
      },
    });
  } catch (error) {
    const fallbackMessage = t(
      'INBOX_MGMT.ADD.IUNGO_CHANNEL.API.ERROR_MESSAGE'
    );
    const errorMessage =
      error?.message || error?.response?.data?.message || fallbackMessage;
    state.serverError = errorMessage;
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.DESC')"
    />
    <form class="flex flex-col gap-4 mx-0" @submit.prevent="createChannel">
      <div class="w-full">
        <label :class="{ error: v$.channelName.$error }">
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.LABEL') }}
          <input
            v-model.trim="state.channelName"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.PLACEHOLDER')"
            @blur="v$.channelName.$touch"
            @input="resetErrors"
          />
          <span v-if="v$.channelName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.CHANNEL_NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.appId.$error }">
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.LABEL') }}
          <input
            v-model.trim="state.appId"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.PLACEHOLDER')"
            @blur="v$.appId.$touch"
            @input="resetErrors"
          />
          <span v-if="v$.appId.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.APP_ID.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.token.$error }">
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.LABEL') }}
          <input
            v-model.trim="state.token"
            type="text"
            :placeholder="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.PLACEHOLDER')"
            @blur="v$.token.$touch"
            @input="resetErrors"
          />
          <span v-if="v$.token.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.TOKEN.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.accountApiKey.$error }">
          {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.LABEL') }}
          <input
            v-model.trim="state.accountApiKey"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.PLACEHOLDER')
            "
            @blur="v$.accountApiKey.$touch"
            @input="resetErrors"
          />
          <span v-if="v$.accountApiKey.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.IUNGO_CHANNEL.ACCOUNT_API_KEY.ERROR') }}
          </span>
        </label>
      </div>

      <p
        v-if="state.serverError"
        class="text-sm text-red-600 dark:text-red-400"
        data-testid="iungo-server-error"
      >
        {{ state.serverError }}
      </p>

      <div class="w-full mt-2">
        <NextButton
          type="submit"
          :is-loading="uiFlags.isCreating"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.IUNGO_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
